import { useState, useEffect, useRef } from 'react';
import { FaPlus } from 'react-icons/fa';
import styles from './PageEdit.module.css';
import ImageSliderSelectorItem from './ImageSliderSelectorItem';
import { UploadFolderIdProvider } from "../util/UploadFolderIdContext";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';

let idCounter = 0;
const genId = () => `img-${idCounter++}-${Date.now()}`;

export default function ImageSliderSelector({ sectionKey, images, updateField }) {
  const uploadFolderId = '1uVJa7ghzMekpR0tLXG7yN9m322qP9alx';
  const safeImages = Array.isArray(images) ? images : [];

  // Local mirror with stable ids so dnd-kit has something to track.
  const [items, setItems] = useState(() => safeImages.map(url => ({ id: genId(), url })));
  const skipNextSync = useRef(false);

  // Only re-sync from props when the change came from OUTSIDE this component
  // (e.g. a fresh DB fetch), not from our own updateField call.
  useEffect(() => {
    if (skipNextSync.current) {
      skipNextSync.current = false;
      return;
    }
    setItems(safeImages.map(url => ({ id: genId(), url })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images]);

  const pushUpdate = (newItems) => {
    setItems(newItems);
    skipNextSync.current = true;
    updateField(sectionKey, 'images', newItems.map(i => i.url));
  };

  const handleAddImage = () => pushUpdate([...items, { id: genId(), url: null }]);
  const handleRemoveImage = (id) => pushUpdate(items.filter(item => item.id !== id));
  const handleImageChange = (id, newUrl) =>
    pushUpdate(items.map(item => (item.id === id ? { ...item, url: newUrl } : item)));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }) // avoid hijacking clicks
  );

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex(i => i.id === active.id);
    const newIndex = items.findIndex(i => i.id === over.id);
    pushUpdate(arrayMove(items, oldIndex, newIndex));
  };

  return (
    <UploadFolderIdProvider folderId={uploadFolderId}>
      <div className={styles.pageHeaderEdit}>
        <h1>Edit Slider</h1>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
            {items.map(item => (
              <ImageSliderSelectorItem
                key={item.id}
                id={item.id}
                image={item.url}
                onChange={(newUrl) => handleImageChange(item.id, newUrl)}
                onRemove={() => handleRemoveImage(item.id)}
              />
            ))}
          </SortableContext>
        </DndContext>

        <div
          className={styles.addImageItem}
          onClick={handleAddImage}
          style={{ cursor: 'pointer', marginTop: '1rem', display: 'inline-block' }}
        >
          <FaPlus size={24} />
        </div>
      </div>
    </UploadFolderIdProvider>
  );
}